import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Package, Shield, Eye, Users, ArrowRight, Send, Truck, MapPin, CheckCircle, Star, Zap, Globe, Heart, Sparkles, Play, ChevronRight } from "lucide-react";
import Header from "@/components/layout/Header";
import heroEmotionalBled from "@/assets/hero-emotional-bled-alt.jpg";

// ============== VARIANTE IMMERSIVE GoBledPro ==============

// Hook pour détecter prefers-reduced-motion
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  
  return prefersReducedMotion;
};

// Composant Blob organique animé - GoBledPro Style
const OrganicBlob = ({ reducedMotion }: { reducedMotion: boolean }) => (
  <div className="relative w-full h-full flex items-center justify-center">
    {/* Blob principal */}
    <svg
      viewBox="0 0 200 200"
      className={`w-[300px] h-[300px] md:w-[400px] md:h-[400px] ${reducedMotion ? '' : 'animate-blob'}`}
      style={{ filter: 'url(#grain)' }}
    >
      <defs>
        {/* Filtre grain subtil */}
        <filter id="grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" result="noise" />
          <feComposite in="SourceGraphic" in2="noise" operator="in" />
          <feBlend in="SourceGraphic" in2="noise" mode="multiply" />
        </filter>
        {/* Dégradé GoBledPro */}
        <linearGradient id="blobGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(160 84% 39%)" stopOpacity="0.35" />
          <stop offset="50%" stopColor="hsl(213 52% 35%)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="hsl(0 70% 50%)" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      <path
        fill="url(#blobGradient)"
        d="M44.7,-76.4C58.9,-69.2,71.8,-58.2,79.4,-44.2C87,-30.2,89.3,-13.2,87.4,2.8C85.5,18.8,79.4,33.8,70.1,46.4C60.8,59,48.3,69.2,34.1,75.8C19.9,82.4,4,85.4,-11.5,83.5C-27,81.6,-42.1,74.8,-54.3,64.5C-66.5,54.2,-75.8,40.4,-80.8,25C-85.8,9.6,-86.5,-7.4,-82.4,-22.8C-78.3,-38.2,-69.4,-52,-57,-62.1C-44.6,-72.2,-28.7,-78.6,-12.4,-79.9C3.9,-81.2,30.5,-77.4,44.7,-76.4Z"
        transform="translate(100 100)"
      />
    </svg>
    
    {/* Icône centrale - Colis */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className={`bg-background/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-border/50 ${reducedMotion ? '' : 'animate-float-slow'}`}>
        <Package className="w-12 h-12 text-primary" />
      </div>
    </div>
  </div>
);

// Variante Immersive GoBledPro
const HeroVariant_ImmersiveAI = () => {
  const reducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section 
      className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-b from-background via-background to-muted/30"
      aria-labelledby="hero-immersive-title"
    >
      {/* Fond subtil avec motif */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      
      <div className="container mx-auto px-4 relative z-10 py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Colonne gauche : Texte + CTA */}
          <div className={reducedMotion ? '' : `transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-primary" aria-hidden="true" />
              <span className="text-sm font-medium text-primary">🚀 Plateforme en beta test</span>
            </div>
            
            {/* H1 - Typo expressive avec clamp */}
            <h1 
              id="hero-immersive-title"
              className="font-heading font-bold text-foreground leading-[1.05] mb-6"
              style={{ 
                fontSize: 'clamp(2.25rem, 5vw + 1rem, 4rem)',
                letterSpacing: '-0.02em'
              }}
            >
              Transport de colis
              <br />
              <span className="relative inline-block">
                <span className="text-[#002395]">Fr</span>
                <span className="text-foreground/80">an</span>
                <span className="text-[#ED2939]">ce</span>
                <span className="text-foreground mx-2">⇄</span>
                <span className="text-[#C1272D]">Mar</span>
                <span className="text-[#006233]">oc</span>
              </span>
              <br />
              <span className="relative z-10 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                en toute confiance
              </span>
            </h1>
            
            {/* Sous-titre */}
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-lg leading-relaxed">
              GoBledPro met en relation particuliers et transporteurs indépendants sélectionnés, 
              pour des envois fiables France ⇄ Maroc.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <Link to="/demande-transport">
                <Button 
                  size="lg" 
                  className={`w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground text-base px-8 h-14 gap-2 font-semibold shadow-lg shadow-primary/20 rounded-xl transition-all ${reducedMotion ? '' : 'hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30'}`}
                  aria-label="Trouver un transporteur pour envoyer un colis"
                >
                  Trouver un transport
                  <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </Button>
              </Link>
              
              <Link to="/annonces-transporteurs">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className={`w-full sm:w-auto text-base px-8 h-14 border-border/60 rounded-xl transition-all ${reducedMotion ? '' : 'hover:-translate-y-0.5'}`}
                  aria-label="Voir les annonces de transporteurs disponibles"
                >
                  Voir les trajets
                </Button>
              </Link>
            </div>
            
            {/* Lien tertiaire */}
            <Link 
              to="/#comment-ca-marche" 
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors group focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 rounded-sm"
              aria-label="En savoir plus sur le fonctionnement de GoBledPro"
            >
              Comment ça marche ?
              <ChevronRight className={`w-4 h-4 transition-transform ${reducedMotion ? '' : 'group-hover:translate-x-1'}`} aria-hidden="true" />
            </Link>
            
            {/* Micro-copy / Réassurance */}
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" aria-hidden="true" />
                Transporteurs vérifiés
              </span>
              <span className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary" aria-hidden="true" />
                Tarifs transparents
              </span>
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" aria-hidden="true" />
                Communauté diaspora
              </span>
            </div>
          </div>
          
          {/* Colonne droite : Blob organique */}
          <div 
            className={`hidden lg:flex items-center justify-center ${reducedMotion ? '' : `transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}`}
            aria-hidden="true"
          >
            <OrganicBlob reducedMotion={reducedMotion} />
          </div>
        </div>
      </div>
      
      {/* CSS pour animations (injecté dans le head via style) */}
      <style>{`
        @keyframes blob-morph {
          0%, 100% { d: path("M44.7,-76.4C58.9,-69.2,71.8,-58.2,79.4,-44.2C87,-30.2,89.3,-13.2,87.4,2.8C85.5,18.8,79.4,33.8,70.1,46.4C60.8,59,48.3,69.2,34.1,75.8C19.9,82.4,4,85.4,-11.5,83.5C-27,81.6,-42.1,74.8,-54.3,64.5C-66.5,54.2,-75.8,40.4,-80.8,25C-85.8,9.6,-86.5,-7.4,-82.4,-22.8C-78.3,-38.2,-69.4,-52,-57,-62.1C-44.6,-72.2,-28.7,-78.6,-12.4,-79.9C3.9,-81.2,30.5,-77.4,44.7,-76.4Z"); }
          50% { d: path("M39.5,-68.2C52.1,-60.1,63.9,-51.1,71.7,-39.1C79.5,-27.1,83.3,-12.1,82.3,2.4C81.3,16.9,75.5,30.9,66.9,42.8C58.3,54.7,46.9,64.5,34,70.8C21.1,77.1,6.7,79.9,-7.8,79.2C-22.3,78.5,-36.9,74.3,-48.9,66.3C-60.9,58.3,-70.3,46.5,-76.3,32.9C-82.3,19.3,-84.9,3.9,-82.7,-10.5C-80.5,-24.9,-73.5,-38.3,-63.1,-48.8C-52.7,-59.3,-38.9,-66.9,-25,-72.3C-11.1,-77.7,2.9,-80.9,16.9,-79.1C30.9,-77.3,44.9,-70.5,39.5,-68.2Z"); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        
        .animate-blob path {
          animation: blob-morph 12s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 4s ease-in-out infinite;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .animate-blob path,
          .animate-float-slow {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
};

// Variation A - Current Hero (baseline)
const HeroVariantA = () => (
  <section className="relative min-h-[70vh] flex items-center pt-6 pb-8 overflow-x-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5">
    <div className="container mx-auto px-4 relative z-10">
      <div className="grid lg:grid-cols-2 gap-5 lg:gap-10 items-center">
        <div className="lg:text-left">
          <div className="inline-flex items-center bg-muted/60 border border-border/40 rounded-lg px-4 py-2 mb-3">
            <span className="text-sm text-muted-foreground font-medium">🚀 Plateforme en beta test</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-3 leading-[1.1]">
            <span className="block">Transport de colis</span>
            <span className="block text-4xl md:text-5xl lg:text-6xl">
              <span className="text-[#002395]">Fr</span><span className="text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">an</span><span className="text-[#ED2939]">ce</span>
              <span className="text-foreground"> ⇄ </span>
              <span className="text-[#C1272D]">Mar</span><span className="text-[#006233]">oc</span>
            </span>
            <span className="block">en toute confiance</span>
          </h1>
          
          <p className="text-base md:text-lg text-foreground/70 mb-5 max-w-xl leading-relaxed">
            GoBledPro met en relation particuliers et transporteurs indépendants sélectionnés, pour des envois fiables France ⇄ Maroc.
          </p>
          
          <Link to="/demande-transport">
            <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground text-base px-8 h-14 gap-2">
              Trouver un transport
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
        
        <div className="hidden lg:flex items-center justify-center">
          <div className="relative w-[300px] h-[300px] rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            <Package className="w-24 h-24 text-primary" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Variation B - More visual impact with split layout
const HeroVariantB = () => (
  <section className="relative min-h-[70vh] flex items-center overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary/80" />
    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZoLTJ2LTRoMnY0em0wLTZoLTJ2LTRoMnY0em0wLTZoLTJ2LTRoMnY0em0wLTZoLTJWNmgydjR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div className="text-white">
          <div className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-4">
            <span className="text-sm font-medium">🚀 Plateforme en beta test</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4 leading-[1.1]">
            Envoyez vos colis<br />
            <span className="text-secondary">France ⇄ Maroc</span><br />
            en toute confiance
          </h1>
          
          <p className="text-lg text-white/80 mb-6 max-w-lg">
            Mise en relation avec des transporteurs indépendants vérifiés. Simple, fiable, transparent.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/demande-transport">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto text-base px-8 h-14 gap-2 font-semibold">
                Envoyer un colis
                <Send className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/annonces-transporteurs">
              <Button size="lg" variant="outline" className="w-full sm:w-auto text-base px-8 h-14 border-white/30 text-white hover:bg-white/10">
                Voir les transporteurs
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center gap-6 mt-6 text-sm text-white/70">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Gratuit</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Sans engagement</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Sécurisé</span>
          </div>
        </div>
        
        <div className="hidden lg:block">
          <div className="relative">
            <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-xl" />
            <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                  <Truck className="w-6 h-6 text-secondary-foreground" />
                </div>
                <div>
                  <p className="font-semibold text-white">+50 transporteurs</p>
                  <p className="text-sm text-white/60">actifs sur la plateforme</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <MapPin className="w-5 h-5 text-secondary" />
                  <span className="text-white/80">Paris → Casablanca</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <MapPin className="w-5 h-5 text-secondary" />
                  <span className="text-white/80">Lyon → Rabat</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <MapPin className="w-5 h-5 text-secondary" />
                  <span className="text-white/80">Marseille → Fès</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Variation C - Minimal, clean, centered
const HeroVariantC = () => (
  <section className="relative min-h-[70vh] flex items-center justify-center text-center overflow-hidden bg-background">
    <div className="absolute inset-0">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full blur-3xl" />
    </div>
    
    <div className="container mx-auto px-4 relative z-10 max-w-4xl">
      <div className="inline-flex items-center bg-primary/10 text-primary rounded-full px-4 py-2 mb-6">
        <span className="text-sm font-medium">🚀 Plateforme en beta test</span>
      </div>
      
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6 leading-tight">
        Le transport de colis<br />
        <span className="bg-gradient-to-r from-[#002395] via-[#ED2939] to-[#006233] bg-clip-text text-transparent">France ⇄ Maroc</span><br />
        simplifié
      </h1>
      
      <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
        Trouvez un transporteur vérifié en quelques clics. GoBledPro connecte la diaspora avec des professionnels de confiance.
      </p>
      
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link to="/demande-transport">
          <Button size="lg" className="text-base px-10 h-14 gap-2 shadow-lg shadow-primary/20">
            Demander un transport
            <ArrowRight className="w-5 h-5" />
          </Button>
        </Link>
        <Link to="/devenir-transporteur">
          <Button size="lg" variant="outline" className="text-base px-10 h-14">
            Je suis transporteur
          </Button>
        </Link>
      </div>
      
      <div className="flex items-center justify-center gap-8 mt-10 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          <span>Transporteurs vérifiés</span>
        </div>
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-primary" />
          <span>Tarifs transparents</span>
        </div>
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          <span>Suivi des envois</span>
        </div>
      </div>
    </div>
  </section>
);

// Variation D - Cards focused
const HeroVariantD = () => (
  <section className="relative min-h-[70vh] flex items-center pt-8 pb-12 overflow-hidden bg-gradient-to-b from-muted/50 to-background">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center mb-10">
        <div className="inline-flex items-center bg-secondary/20 text-secondary-foreground rounded-lg px-4 py-2 mb-4">
          <span className="text-sm font-medium">🚀 Nouveau : Plateforme en beta test</span>
        </div>
        
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
          Transport colis <span className="text-primary">France ⇄ Maroc</span>
        </h1>
        
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Que souhaitez-vous faire ?
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <Link to="/demande-transport" className="group">
          <div className="bg-card border border-border rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:border-primary/50 hover:-translate-y-1">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
              <Package className="w-7 h-7 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Envoyer un colis</h2>
            <p className="text-muted-foreground mb-4">
              Trouvez un transporteur de confiance pour acheminer votre colis vers le Maroc ou la France.
            </p>
            <span className="inline-flex items-center text-primary font-medium gap-1 group-hover:gap-2 transition-all">
              Commencer <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
        
        <Link to="/devenir-transporteur" className="group">
          <div className="bg-card border border-border rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:border-secondary/50 hover:-translate-y-1">
            <div className="w-14 h-14 rounded-xl bg-secondary/20 flex items-center justify-center mb-4 group-hover:bg-secondary/30 transition-colors">
              <Truck className="w-7 h-7 text-secondary-foreground" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Devenir transporteur</h2>
            <p className="text-muted-foreground mb-4">
              Rejoignez notre réseau de transporteurs vérifiés et développez votre activité.
            </p>
            <span className="inline-flex items-center text-secondary-foreground font-medium gap-1 group-hover:gap-2 transition-all">
              S'inscrire <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      </div>
      
      <div className="flex items-center justify-center gap-6 mt-10 text-sm text-muted-foreground">
        <span>✓ Inscription gratuite</span>
        <span>✓ Sans engagement</span>
        <span>✓ Transporteurs vérifiés</span>
      </div>
    </div>
  </section>
);

// ============== NOUVELLES VARIANTES TRANSFORMÉES ==============

// Variation E - Brutalist / Bold Typography
const HeroVariantE = () => (
  <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-foreground text-background">
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-0 left-0 w-full h-full" style={{
        backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, hsl(var(--background)) 50px, hsl(var(--background)) 51px)`
      }} />
    </div>
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-5xl">
        <div className="mb-8">
          <span className="inline-block bg-secondary text-secondary-foreground px-4 py-2 text-sm font-bold uppercase tracking-widest">
            Beta 2025
          </span>
        </div>
        
        <h1 className="text-5xl md:text-7xl lg:text-[120px] font-heading font-black leading-[0.85] tracking-tight mb-8">
          COLIS
          <br />
          <span className="text-secondary">FR→MA</span>
        </h1>
        
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="flex-1">
            <p className="text-xl md:text-2xl text-background/70 font-light leading-relaxed max-w-md">
              La diaspora mérite mieux. Transporteurs vérifiés. Tarifs clairs. Zéro galère.
            </p>
          </div>
          
          <div className="flex flex-col gap-4">
            <Link to="/demande-transport">
              <Button size="lg" variant="secondary" className="text-lg px-10 h-16 font-bold uppercase tracking-wide">
                Envoyer maintenant
              </Button>
            </Link>
            <Link to="/annonces-transporteurs">
              <Button size="lg" variant="outline" className="text-lg px-10 h-16 border-2 border-background/30 text-background hover:bg-background/10 font-medium">
                Voir les trajets
              </Button>
            </Link>
          </div>
        </div>
        
        <div className="mt-16 pt-8 border-t border-background/20">
          <div className="flex flex-wrap gap-12 text-sm uppercase tracking-widest text-background/50">
            <span>+50 transporteurs</span>
            <span>100% vérifiés</span>
            <span>Depuis 2024</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Variation F - Glassmorphism / Futuristic
const HeroVariantF = () => (
  <section className="relative min-h-[80vh] flex items-center overflow-hidden">
    {/* Animated gradient background */}
    <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]" />
    <div className="absolute inset-0">
      <div className="absolute top-20 left-20 w-72 h-72 bg-[#e94560] rounded-full filter blur-[120px] opacity-30 animate-pulse" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#0f3460] rounded-full filter blur-[150px] opacity-50" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00b4d8] rounded-full filter blur-[200px] opacity-20" />
    </div>
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full px-6 py-3 mb-8">
          <Sparkles className="w-4 h-4 text-[#e94560]" />
          <span className="text-white/80 text-sm">Nouvelle génération de transport</span>
        </div>
        
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6 leading-tight">
          Le futur du transport
          <br />
          <span className="bg-gradient-to-r from-[#00b4d8] via-[#e94560] to-[#ffd60a] bg-clip-text text-transparent">
            France ⇄ Maroc
          </span>
        </h1>
        
        <p className="text-lg md:text-xl text-white/60 mb-10 max-w-2xl mx-auto">
          Une plateforme intelligente qui connecte vos colis aux meilleurs transporteurs. Rapide, sécurisé, transparent.
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Link to="/demande-transport">
            <Button size="lg" className="bg-gradient-to-r from-[#e94560] to-[#00b4d8] hover:opacity-90 text-white text-lg px-10 h-16 rounded-xl border-0 shadow-lg shadow-[#e94560]/30">
              Commencer gratuitement
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <Link to="/annonces-transporteurs">
            <Button size="lg" variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 text-lg px-8 h-16 gap-2">
              <Play className="w-5 h-5" />
              Découvrir
            </Button>
          </Link>
        </div>
        
        {/* Glass cards stats */}
        <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
          {[
            { value: '50+', label: 'Transporteurs' },
            { value: '24h', label: 'Réponse moyenne' },
            { value: '100%', label: 'Vérifiés' }
          ].map((stat, i) => (
            <div key={i} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-white/50">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Variation G - Editorial / Magazine Style
const HeroVariantG = () => (
  <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#faf9f6]">
    <div className="container mx-auto px-4 relative z-10">
      <div className="grid lg:grid-cols-12 gap-8 items-center">
        {/* Left content */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-foreground/20 max-w-[60px]" />
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-foreground/60">
              Transport de confiance
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-normal text-foreground leading-[1.1] mb-6">
            Vos colis traversent
            <br />
            la <span className="italic">Méditerranée</span>,
            <br />
            pas vos soucis.
          </h1>
          
          <p className="text-lg text-foreground/60 leading-relaxed max-w-lg mb-8 font-light">
            GoBledPro réunit une communauté de transporteurs vérifiés pour des envois France-Maroc en toute sérénité. Parce que vos proches méritent le meilleur.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/demande-transport">
              <Button size="lg" className="bg-foreground hover:bg-foreground/90 text-background rounded-none text-sm px-8 h-14 font-medium tracking-wide">
                DEMANDER UN TRANSPORT
              </Button>
            </Link>
            <Link to="/devenir-transporteur">
              <Button size="lg" variant="outline" className="rounded-none text-sm px-8 h-14 border-foreground/30 font-medium tracking-wide">
                ESPACE TRANSPORTEUR
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Right visual */}
        <div className="lg:col-span-5 relative">
          <div className="aspect-[4/5] bg-gradient-to-br from-primary/20 to-secondary/20 relative">
            <div className="absolute inset-4 border border-foreground/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Globe className="w-20 h-20 text-foreground/20 mx-auto mb-4" />
                <div className="text-6xl font-serif text-foreground/10">FR⇄MA</div>
              </div>
            </div>
            {/* Floating badge */}
            <div className="absolute -left-6 bottom-20 bg-white shadow-xl p-4 border border-foreground/10">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full bg-muted border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="text-sm font-medium">+50 transporteurs</div>
                  <div className="text-xs text-muted-foreground">actifs ce mois</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Variation H - Playful / Rounded / Friendly
const HeroVariantH = () => (
  <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-b from-[#fff5eb] to-[#ffe4cc]">
    {/* Decorative shapes */}
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#ff6b35] rounded-full opacity-10" />
      <div className="absolute top-1/2 -left-40 w-96 h-96 bg-[#004e89] rounded-full opacity-10" />
      <div className="absolute -bottom-20 right-1/3 w-64 h-64 bg-[#1a659e] rounded-full opacity-10" />
    </div>
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        {/* Emoji header */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <span className="text-4xl">🇫🇷</span>
          <div className="flex items-center gap-2 bg-white/80 backdrop-blur rounded-full px-6 py-3 shadow-lg">
            <Heart className="w-5 h-5 text-[#ff6b35]" />
            <span className="font-medium text-foreground">Transport familial</span>
          </div>
          <span className="text-4xl">🇲🇦</span>
        </div>
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-[#1a3a5c] mb-6 leading-tight">
          Envoyez de l'amour
          <br />
          <span className="relative inline-block">
            <span className="relative z-10">au bled</span>
            <span className="absolute bottom-2 left-0 right-0 h-4 bg-[#ff6b35]/30 -z-10 rounded-full" />
          </span>
          , tout simplement
        </h1>
        
        <p className="text-lg md:text-xl text-[#1a3a5c]/70 mb-10 max-w-xl mx-auto">
          Des transporteurs de confiance pour que vos colis arrivent à bon port. Comme à la maison 🏠
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Link to="/demande-transport">
            <Button size="lg" className="bg-[#ff6b35] hover:bg-[#ff6b35]/90 text-white text-lg px-10 h-16 rounded-full shadow-xl shadow-[#ff6b35]/30 gap-2">
              <Package className="w-5 h-5" />
              Envoyer un colis
            </Button>
          </Link>
          <Link to="/annonces-transporteurs">
            <Button size="lg" variant="outline" className="text-lg px-10 h-16 rounded-full border-2 border-[#1a3a5c]/20 text-[#1a3a5c]">
              Voir les trajets 🚐
            </Button>
          </Link>
        </div>
        
        {/* Trust badges playful */}
        <div className="flex flex-wrap items-center justify-center gap-4">
          {[
            { emoji: '✅', text: 'Vérifiés' },
            { emoji: '💰', text: 'Pas cher' },
            { emoji: '⚡', text: 'Rapide' },
            { emoji: '🔒', text: 'Sécurisé' }
          ].map((badge, i) => (
            <div key={i} className="flex items-center gap-2 bg-white rounded-full px-5 py-3 shadow-md">
              <span className="text-xl">{badge.emoji}</span>
              <span className="font-medium text-[#1a3a5c]">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Variation I - Emotional Poster / Western Union Style
const HeroVariantI = () => (
  <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-[#1a1a2e] via-[#2d1f3d] to-[#1a1a2e]">
    {/* Subtle pattern overlay */}
    <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }} />
    
    <div className="container mx-auto px-4 relative z-10">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Left: Text content */}
        <div className="order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 bg-secondary/20 text-secondary rounded-full px-4 py-2 mb-6">
            <Heart className="w-4 h-4" />
            <span className="text-sm font-medium">Plus qu'un colis, un lien</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-[1.1]">
            Vos proches au bled
            <br />
            <span className="text-secondary">n'attendent que vous</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/70 mb-8 max-w-lg leading-relaxed">
            Chaque colis raconte une histoire. GoBledPro connecte la diaspora avec des transporteurs de confiance pour que vos envois arrivent avec tout l'amour que vous y mettez.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Link to="/demande-transport">
              <Button size="lg" className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg px-10 h-16 gap-2 font-semibold shadow-xl shadow-secondary/30">
                Envoyer un colis
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/annonces-transporteurs">
              <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 h-16 border-white/20 text-white hover:bg-white/10">
                Voir les trajets
              </Button>
            </Link>
          </div>
          
          {/* Trust indicators */}
          <div className="flex flex-wrap gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-secondary" />
              <span>Transporteurs vérifiés</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-secondary" />
              <span>Envois sécurisés</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-secondary" />
              <span>100% diaspora</span>
            </div>
          </div>
        </div>
        
        {/* Right: Emotional image */}
        <div className="order-1 lg:order-2 relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e]/80 via-transparent to-transparent z-10" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a2e]/50 via-transparent to-transparent z-10 lg:hidden" />
            
            <img 
              src={heroEmotionalBled} 
              alt="Famille recevant un colis au Maroc" 
              className="w-full h-[400px] lg:h-[500px] object-cover"
            />
            
            {/* Floating caption */}
            <div className="absolute bottom-6 left-6 right-6 z-20">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                <p className="text-white text-sm italic">
                  "Le sourire de ma mère quand elle reçoit le colis... ça n'a pas de prix."
                </p>
                <p className="text-white/60 text-xs mt-1">— Karim, Paris</p>
              </div>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-secondary/20 rounded-full blur-2xl" />
          <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/20 rounded-full blur-3xl" />
        </div>
      </div>
    </div>
  </section>
);

// Variation K - Neo-Retro Modern (60s-70s inspired, warm & human)
const HeroVariantK_NeoRetro = () => {
  const reducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section 
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={{ backgroundColor: '#1a3f4c' }} // Bleu pétrole
      aria-labelledby="hero-neoretro-title"
    >
      {/* Motif rétro subtil en fond */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, #fff 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} />
      
      {/* Formes organiques décoratives */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {/* Cercle décoratif haut droite */}
        <div 
          className={`absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-20 ${reducedMotion ? '' : 'animate-pulse-slow'}`}
          style={{ backgroundColor: '#d4a853' }} // Moutarde doux
        />
        {/* Cercle décoratif bas gauche */}
        <div 
          className={`absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-15 ${reducedMotion ? '' : 'animate-pulse-slow'}`}
          style={{ backgroundColor: '#e8dcc4', animationDelay: '2s' }} // Crème
        />
        {/* Forme ondulée subtile */}
        <svg 
          className="absolute bottom-0 left-0 right-0 w-full h-24 opacity-10" 
          viewBox="0 0 1440 100" 
          preserveAspectRatio="none"
        >
          <path 
            fill="#e8dcc4" 
            d="M0,50 C360,100 720,0 1080,50 C1260,75 1380,25 1440,50 L1440,100 L0,100 Z"
          />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10 py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Colonne gauche : Texte */}
          <div className={reducedMotion ? '' : `transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Badge rétro - style pastille vintage */}
            <div 
              className="inline-flex items-center gap-2 px-5 py-2.5 mb-8 border-2"
              style={{ 
                backgroundColor: '#d4a853',
                borderColor: '#b8923d',
                color: '#1a3f4c',
                borderRadius: '4px',
                transform: 'rotate(-1deg)',
                boxShadow: '3px 3px 0 rgba(0,0,0,0.2)'
              }}
            >
              <span className="text-base font-bold tracking-widest uppercase" style={{ fontFamily: 'system-ui' }}>
                ★ Beta test ★
              </span>
            </div>
            
            {/* H1 - Police ABRIL FATFACE rétro expressive - Composition affiche */}
            <h1 
              id="hero-neoretro-title"
              className="font-retro leading-[0.95] mb-8"
              style={{ 
                color: '#f5f0e6',
                letterSpacing: '0.01em'
              }}
            >
              {/* Ligne 1 - Plus petite */}
              <span 
                className="block"
                style={{ 
                  fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.5rem)',
                  marginBottom: '0.25rem'
                }}
              >
                Transport de
              </span>
              
              {/* Ligne 2 - DOMINANT */}
              <span 
                className="block relative"
                style={{ 
                  fontSize: 'clamp(3rem, 8vw + 1rem, 6rem)',
                  lineHeight: 1
                }}
              >
                <span style={{ color: '#f5f0e6' }}>COLIS</span>
              </span>
              
              {/* Ligne 3 - France ⇄ Maroc avec highlight rétro */}
              <span 
                className="block mt-3 relative"
                style={{ 
                  fontSize: 'clamp(1.8rem, 4vw + 0.5rem, 3rem)'
                }}
              >
                <span 
                  className="relative inline-block px-3 py-1"
                  style={{ 
                    backgroundColor: 'rgba(212, 168, 83, 0.25)',
                    borderRadius: '8px'
                  }}
                >
                  <span style={{ color: '#6ba3be' }}>France</span>
                  <span className="mx-2" style={{ color: '#d4a853' }}>⇄</span>
                  <span style={{ color: '#c75b4a' }}>Maroc</span>
                </span>
              </span>
              
              {/* Ligne 4 - "en toute confiance" avec soulignement épais */}
              <span 
                className="block mt-4 relative inline-block"
                style={{ 
                  fontSize: 'clamp(1.4rem, 3vw + 0.5rem, 2.2rem)',
                  color: '#d4a853'
                }}
              >
                en toute confiance
                {/* Soulignement épais rétro */}
                <span 
                  className="absolute -bottom-2 left-0 right-0 h-3"
                  style={{ 
                    backgroundColor: '#d4a853',
                    opacity: 0.3,
                    borderRadius: '4px'
                  }}
                  aria-hidden="true"
                />
              </span>
            </h1>
            
            {/* Sous-titre */}
            <p 
              className="text-lg md:text-xl mb-8 max-w-lg leading-relaxed"
              style={{ color: 'rgba(245, 240, 230, 0.75)' }}
            >
              GoBledPro met en relation particuliers et transporteurs indépendants sélectionnés, 
              pour des envois fiables entre la France et le Maroc.
            </p>
            
            {/* CTAs style rétro - Boutons plus massifs avec coins arrondis */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Link to="/demande-transport">
                <Button 
                  size="lg" 
                  className={`w-full sm:w-auto text-lg px-12 h-[4.5rem] font-bold gap-3 border-0 transition-all ${reducedMotion ? '' : 'hover:-translate-y-1 hover:shadow-2xl'}`}
                  style={{ 
                    backgroundColor: '#d4a853', 
                    color: '#1a3f4c',
                    borderRadius: '2rem',
                    boxShadow: '0 8px 24px -8px rgba(212, 168, 83, 0.5), 4px 4px 0 rgba(0,0,0,0.15)',
                    fontFamily: 'system-ui'
                  }}
                  aria-label="Trouver un transporteur pour envoyer un colis"
                >
                  <Package className="w-6 h-6" aria-hidden="true" />
                  Trouver un transport
                </Button>
              </Link>
              
              <Link to="/annonces-transporteurs">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className={`w-full sm:w-auto text-lg px-10 h-[4.5rem] font-semibold transition-all border-2 ${reducedMotion ? '' : 'hover:-translate-y-1'}`}
                  style={{ 
                    borderColor: 'rgba(245, 240, 230, 0.4)', 
                    color: '#f5f0e6',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    borderRadius: '2rem',
                    fontFamily: 'system-ui'
                  }}
                  aria-label="Voir les annonces de transporteurs disponibles"
                >
                  Voir les trajets
                </Button>
              </Link>
            </div>
            
            {/* Lien tertiaire */}
            <Link 
              to="/#comment-ca-marche" 
              className="inline-flex items-center gap-1 text-sm transition-colors group focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-sm"
              style={{ color: 'rgba(245, 240, 230, 0.6)' }}
              aria-label="En savoir plus sur le fonctionnement"
            >
              Comment ça marche ?
              <ChevronRight className={`w-4 h-4 transition-transform ${reducedMotion ? '' : 'group-hover:translate-x-1'}`} aria-hidden="true" />
            </Link>
          </div>
          
          {/* Colonne droite : Cartes rétro */}
          <div 
            className={`hidden lg:block ${reducedMotion ? '' : `transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}`}
            aria-hidden="true"
          >
            <div className="relative">
              {/* Carte principale crème */}
              <div 
                className="rounded-[2rem] p-8 shadow-2xl border-4"
                style={{ 
                  backgroundColor: '#f5f0e6', 
                  borderColor: 'rgba(212, 168, 83, 0.3)',
                  boxShadow: '0 25px 60px -15px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.5)'
                }}
              >
                {/* En-tête carte */}
                <div className="flex items-center gap-4 mb-6 pb-5 border-b-2" style={{ borderColor: 'rgba(26, 63, 76, 0.1)' }}>
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: '#1a3f4c' }}
                  >
                    <Truck className="w-7 h-7" style={{ color: '#d4a853' }} />
                  </div>
                  <div>
                    <p className="font-bold text-lg" style={{ color: '#1a3f4c' }}>+50 transporteurs</p>
                    <p className="text-sm" style={{ color: 'rgba(26, 63, 76, 0.6)' }}>actifs sur la plateforme</p>
                  </div>
                </div>
                
                {/* Liste trajets */}
                <div className="space-y-3">
                  {[
                    { from: 'Paris', to: 'Casablanca' },
                    { from: 'Lyon', to: 'Rabat' },
                    { from: 'Marseille', to: 'Fès' }
                  ].map((route, i) => (
                    <div 
                      key={i}
                      className={`flex items-center gap-3 p-4 rounded-2xl transition-all ${reducedMotion ? '' : 'hover:scale-[1.02]'}`}
                      style={{ 
                        backgroundColor: 'rgba(26, 63, 76, 0.08)',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: '#d4a853' }}
                      >
                        <MapPin className="w-5 h-5" style={{ color: '#1a3f4c' }} />
                      </div>
                      <span className="font-semibold" style={{ color: '#1a3f4c' }}>
                        {route.from} → {route.to}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Footer carte */}
                <div className="mt-6 pt-5 border-t-2 flex items-center justify-between" style={{ borderColor: 'rgba(26, 63, 76, 0.1)' }}>
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5" style={{ color: '#d4a853', fill: '#d4a853' }} />
                    <span className="font-bold" style={{ color: '#1a3f4c' }}>4.8/5</span>
                    <span className="text-sm" style={{ color: 'rgba(26, 63, 76, 0.6)' }}>avis clients</span>
                  </div>
                  <div 
                    className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                    style={{ backgroundColor: 'rgba(26, 63, 76, 0.1)', color: '#1a3f4c' }}
                  >
                    Vérifiés
                  </div>
                </div>
              </div>
              
              {/* Badge flottant décoratif */}
              <div 
                className={`absolute -top-4 -right-4 rounded-2xl px-4 py-3 shadow-xl ${reducedMotion ? '' : 'animate-float-slow'}`}
                style={{ backgroundColor: '#d4a853', color: '#1a3f4c' }}
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  <span className="font-bold text-sm">Réponse 24h</span>
                </div>
              </div>
              
              {/* Cercle décoratif derrière la carte */}
              <div 
                className="absolute -z-10 -bottom-8 -left-8 w-40 h-40 rounded-full opacity-30"
                style={{ backgroundColor: '#d4a853' }}
              />
            </div>
          </div>
        </div>
        
        {/* Réassurance en bas - style rétro */}
        <div 
          className={`mt-12 pt-8 border-t ${reducedMotion ? '' : `transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}`}
          style={{ borderColor: 'rgba(245, 240, 230, 0.15)' }}
        >
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {[
              { icon: Shield, label: 'Transporteurs vérifiés' },
              { icon: Eye, label: 'Tarifs transparents' },
              { icon: Users, label: 'Communauté diaspora' },
              { icon: Heart, label: '100% confiance' }
            ].map(({ icon: Icon, label }, i) => (
              <div 
                key={i}
                className="flex items-center gap-3"
              >
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(212, 168, 83, 0.2)' }}
                >
                  <Icon className="w-5 h-5" style={{ color: '#d4a853' }} />
                </div>
                <span className="text-sm font-medium" style={{ color: 'rgba(245, 240, 230, 0.8)' }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* CSS pour animations rétro */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .animate-pulse-slow,
          .animate-float-slow {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
};

// Variation L - Neo-Retro V1 (version précédente plus simple)
const HeroVariantL_NeoRetroV1 = () => {
  const reducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section 
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={{ backgroundColor: '#1a3f4c' }}
      aria-labelledby="hero-neoretro-v1-title"
    >
      {/* Motif rétro subtil en fond */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, #fff 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} />
      
      {/* Formes organiques décoratives */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div 
          className={`absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-20 ${reducedMotion ? '' : 'animate-pulse-slow'}`}
          style={{ backgroundColor: '#d4a853' }}
        />
        <div 
          className={`absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-15 ${reducedMotion ? '' : 'animate-pulse-slow'}`}
          style={{ backgroundColor: '#e8dcc4', animationDelay: '2s' }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10 py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Colonne gauche : Texte */}
          <div className={reducedMotion ? '' : `transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Badge rétro simple */}
            <div 
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6"
              style={{ 
                backgroundColor: 'rgba(212, 168, 83, 0.2)',
                border: '1px solid rgba(212, 168, 83, 0.4)'
              }}
            >
              <span className="text-sm font-medium" style={{ color: '#d4a853' }}>
                🚀 Plateforme en beta test
              </span>
            </div>
            
            {/* H1 - Style rétro avec police arrondie */}
            <h1 
              id="hero-neoretro-v1-title"
              className="font-heading font-bold leading-[1.1] mb-6"
              style={{ 
                fontSize: 'clamp(2.25rem, 5vw + 1rem, 4rem)',
                color: '#f5f0e6',
                letterSpacing: '-0.01em'
              }}
            >
              <span className="block">Transport de colis</span>
              <span className="block mt-2">
                <span style={{ color: '#6ba3be' }}>France</span>
                <span className="mx-3" style={{ color: '#d4a853' }}>⇄</span>
                <span style={{ color: '#c75b4a' }}>Maroc</span>
              </span>
              <span className="block mt-2" style={{ color: '#d4a853' }}>
                en toute confiance
              </span>
            </h1>
            
            {/* Sous-titre */}
            <p 
              className="text-lg md:text-xl mb-8 max-w-lg leading-relaxed"
              style={{ color: 'rgba(245, 240, 230, 0.75)' }}
            >
              GoBledPro met en relation particuliers et transporteurs indépendants sélectionnés, 
              pour des envois fiables entre la France et le Maroc.
            </p>
            
            {/* CTAs style rétro avec coins arrondis */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Link to="/demande-transport">
                <Button 
                  size="lg" 
                  className={`w-full sm:w-auto text-base px-8 h-14 gap-2 font-semibold border-0 transition-all ${reducedMotion ? '' : 'hover:-translate-y-0.5 hover:shadow-xl'}`}
                  style={{ 
                    backgroundColor: '#d4a853', 
                    color: '#1a3f4c',
                    borderRadius: '9999px',
                    boxShadow: '0 4px 14px -4px rgba(212, 168, 83, 0.5)'
                  }}
                  aria-label="Trouver un transporteur pour envoyer un colis"
                >
                  <Package className="w-5 h-5" aria-hidden="true" />
                  Trouver un transport
                </Button>
              </Link>
              
              <Link to="/annonces-transporteurs">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className={`w-full sm:w-auto text-base px-8 h-14 transition-all ${reducedMotion ? '' : 'hover:-translate-y-0.5'}`}
                  style={{ 
                    borderColor: 'rgba(245, 240, 230, 0.3)', 
                    color: '#f5f0e6',
                    backgroundColor: 'transparent',
                    borderRadius: '9999px'
                  }}
                  aria-label="Voir les annonces de transporteurs disponibles"
                >
                  Voir les trajets
                </Button>
              </Link>
            </div>
            
            {/* Réassurance */}
            <div className="flex flex-wrap gap-4 text-sm" style={{ color: 'rgba(245, 240, 230, 0.6)' }}>
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" style={{ color: '#d4a853' }} />
                Transporteurs vérifiés
              </span>
              <span className="flex items-center gap-2">
                <Eye className="w-4 h-4" style={{ color: '#d4a853' }} />
                Tarifs transparents
              </span>
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" style={{ color: '#d4a853' }} />
                Communauté diaspora
              </span>
            </div>
          </div>
          
          {/* Colonne droite : Cartes rétro */}
          <div 
            className={`hidden lg:block ${reducedMotion ? '' : `transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}`}
            aria-hidden="true"
          >
            <div className="relative">
              {/* Carte principale crème */}
              <div 
                className="rounded-3xl p-8 shadow-2xl"
                style={{ 
                  backgroundColor: '#f5f0e6',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
                }}
              >
                {/* En-tête carte */}
                <div className="flex items-center gap-4 mb-6 pb-5 border-b" style={{ borderColor: 'rgba(26, 63, 76, 0.1)' }}>
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#1a3f4c' }}
                  >
                    <Truck className="w-6 h-6" style={{ color: '#d4a853' }} />
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: '#1a3f4c' }}>+50 transporteurs</p>
                    <p className="text-sm" style={{ color: 'rgba(26, 63, 76, 0.6)' }}>actifs sur la plateforme</p>
                  </div>
                </div>
                
                {/* Liste trajets */}
                <div className="space-y-3">
                  {[
                    { from: 'Paris', to: 'Casablanca' },
                    { from: 'Lyon', to: 'Rabat' },
                    { from: 'Marseille', to: 'Fès' }
                  ].map((route, i) => (
                    <div 
                      key={i}
                      className={`flex items-center gap-3 p-4 rounded-xl transition-all ${reducedMotion ? '' : 'hover:scale-[1.02]'}`}
                      style={{ backgroundColor: 'rgba(26, 63, 76, 0.06)' }}
                    >
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: '#d4a853' }}
                      >
                        <MapPin className="w-4 h-4" style={{ color: '#1a3f4c' }} />
                      </div>
                      <span className="font-medium" style={{ color: '#1a3f4c' }}>
                        {route.from} → {route.to}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Footer carte */}
                <div className="mt-6 pt-5 border-t flex items-center justify-between" style={{ borderColor: 'rgba(26, 63, 76, 0.1)' }}>
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5" style={{ color: '#d4a853', fill: '#d4a853' }} />
                    <span className="font-semibold" style={{ color: '#1a3f4c' }}>4.8/5</span>
                    <span className="text-sm" style={{ color: 'rgba(26, 63, 76, 0.6)' }}>avis clients</span>
                  </div>
                </div>
              </div>
              
              {/* Badge flottant */}
              <div 
                className={`absolute -top-4 -right-4 rounded-xl px-4 py-3 shadow-lg ${reducedMotion ? '' : 'animate-float-slow'}`}
                style={{ backgroundColor: '#d4a853', color: '#1a3f4c' }}
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  <span className="font-semibold text-sm">Réponse 24h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CSS pour animations */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .animate-pulse-slow,
          .animate-float-slow {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
};

// Variation M - Neo-Retro Original (fidèle à la capture)
const HeroVariantM_NeoRetroOriginal = () => {
  const reducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section 
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={{ backgroundColor: '#1e4959' }}
      aria-labelledby="hero-neoretro-original-title"
    >
      {/* Formes organiques en arrière-plan */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div 
          className={`absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-30 blur-3xl ${reducedMotion ? '' : 'animate-pulse-slow'}`}
          style={{ backgroundColor: '#2a5f6f', transform: 'translate(20%, -30%)' }}
        />
        <div 
          className={`absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-20 blur-2xl ${reducedMotion ? '' : 'animate-pulse-slow'}`}
          style={{ backgroundColor: '#3a7080', transform: 'translate(-30%, 30%)', animationDelay: '3s' }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10 py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Colonne gauche : Texte */}
          <div className={reducedMotion ? '' : `transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Badge beta orange/corail avec icône camion */}
            <div 
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 mb-6"
              style={{ 
                backgroundColor: '#c75b4a',
                color: '#fff'
              }}
            >
              <Truck className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm font-semibold tracking-wide uppercase">
                Beta test — Rejoignez-nous
              </span>
            </div>
            
            {/* H1 - Titre principal - Police display arrondie rétro-moderne */}
            <h1 
              id="hero-neoretro-original-title"
              className="font-display leading-[1.0] mb-6"
              style={{ 
                fontSize: 'clamp(2.4rem, 5.8vw + 0.9rem, 4.25rem)',
                letterSpacing: '-0.01em'
              }}
            >
              <span className="block font-black whitespace-nowrap" style={{ color: '#ffffff' }}>Transport de colis</span>
              <span className="block mt-1 font-black">
                <span style={{ color: '#7cb9d4' }}>France</span>
                <span className="mx-2" style={{ color: '#d4a853' }}>⇄</span>
                <span style={{ color: '#c75b4a' }}>Maroc</span>
              </span>
              <span className="block mt-1 font-black whitespace-nowrap">
                <span style={{ color: '#ffffff' }}>en toute</span>{' '}
                <span className="relative inline-block">
                  <span style={{ color: '#d4a853' }}>confiance</span>
                  <span 
                    className="absolute left-0 -bottom-1 h-1 rounded-full w-full"
                    style={{ backgroundColor: '#ffffff', opacity: 0.6 }}
                    aria-hidden="true"
                  />
                </span>
              </span>
            </h1>
            
            {/* Sous-titre */}
            <p 
              className="text-base md:text-lg mb-8 max-w-lg leading-relaxed"
              style={{ color: '#f5f5f5' }}
            >
              GoBledPro met en relation particuliers et transporteurs indépendants sélectionnés, 
              pour des envois fiables entre la France et le Maroc.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demande-transport">
                <Button 
                  size="lg" 
                  className={`w-full sm:w-auto text-base px-8 h-14 gap-2 font-semibold border-0 transition-all ${reducedMotion ? '' : 'hover:-translate-y-0.5 hover:shadow-xl'}`}
                  style={{ 
                    backgroundColor: '#d4a853', 
                    color: '#1e4959',
                    borderRadius: '9999px',
                    boxShadow: '0 4px 14px -4px rgba(212, 168, 83, 0.4)'
                  }}
                  aria-label="Trouver un transporteur pour envoyer un colis"
                >
                  <Package className="w-5 h-5" aria-hidden="true" />
                  Trouver un transport
                </Button>
              </Link>
              
              <Link to="/annonces-transporteurs">
                <Button 
                  size="lg" 
                  className={`w-full sm:w-auto text-base px-8 h-14 transition-all ${reducedMotion ? '' : 'hover:-translate-y-0.5'}`}
                  style={{ 
                    backgroundColor: '#1e4959',
                    border: '1px solid rgba(232, 228, 220, 0.3)', 
                    color: '#e8e4dc',
                    borderRadius: '9999px'
                  }}
                  aria-label="Voir les annonces de transporteurs disponibles"
                >
                  Voir les trajets
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Colonne droite : Carte crème */}
          <div 
            className={`hidden lg:block ${reducedMotion ? '' : `transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}`}
            aria-hidden="true"
          >
            <div className="relative">
              {/* Carte principale */}
              <div 
                className="rounded-2xl p-6 shadow-2xl"
                style={{ 
                  backgroundColor: '#f8f6f1',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)'
                }}
              >
                {/* En-tête avec icône camion */}
                <div className="flex items-center gap-4 mb-5 pb-4 border-b" style={{ borderColor: 'rgba(30, 73, 89, 0.1)' }}>
                  <div 
                    className="w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#1e4959' }}
                  >
                    <Truck className="w-5 h-5" style={{ color: '#d4a853' }} />
                  </div>
                  <div>
                    <p className="font-semibold text-base" style={{ color: '#1e4959' }}>+50 transporteurs</p>
                    <p className="text-sm" style={{ color: 'rgba(30, 73, 89, 0.6)' }}>actifs sur la plateforme</p>
                  </div>
                </div>
                
                {/* Liste trajets */}
                <div className="space-y-3">
                  {[
                    { from: 'Paris', to: 'Casablanca' },
                    { from: 'Lyon', to: 'Rabat' },
                    { from: 'Marseille', to: 'Fès' }
                  ].map((route, i) => (
                    <div 
                      key={i}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${reducedMotion ? '' : 'hover:scale-[1.01]'}`}
                      style={{ 
                        backgroundColor: '#fff',
                        borderColor: 'rgba(30, 73, 89, 0.08)'
                      }}
                    >
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(212, 168, 83, 0.15)' }}
                      >
                        <MapPin className="w-4 h-4" style={{ color: '#d4a853' }} />
                      </div>
                      <span className="font-medium" style={{ color: '#1e4959' }}>
                        {route.from} → {route.to}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Footer avec rating et badge VÉRIFIÉS */}
                <div className="mt-5 pt-4 border-t flex items-center justify-between" style={{ borderColor: 'rgba(30, 73, 89, 0.1)' }}>
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5" style={{ color: '#d4a853', fill: '#d4a853' }} />
                    <span className="font-bold" style={{ color: '#1e4959' }}>4.8/5</span>
                    <span className="text-sm" style={{ color: 'rgba(30, 73, 89, 0.6)' }}>avis clients</span>
                  </div>
                  <span 
                    className="text-xs font-semibold uppercase px-3 py-1 rounded-full"
                    style={{ 
                      backgroundColor: 'rgba(30, 73, 89, 0.1)',
                      color: '#1e4959',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Vérifiés
                  </span>
                </div>
              </div>
              
              {/* Badge flottant "Réponse 24h" */}
              <div 
                className={`absolute -top-3 -right-3 rounded-xl px-4 py-2.5 shadow-lg ${reducedMotion ? '' : 'animate-float-slow'}`}
                style={{ 
                  backgroundColor: '#48a89a',
                  color: '#fff'
                }}
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  <span className="font-semibold text-sm">Réponse 24h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CSS pour animations */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 10s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 4s ease-in-out infinite;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .animate-pulse-slow,
          .animate-float-slow {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
};

const HeroTest = () => {
  const [activeVariant, setActiveVariant] = useState<'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L' | 'M'>('M');

  const variants = {
    A: { name: 'Actuel (baseline)', component: <HeroVariantA /> },
    B: { name: 'Impact visuel (fond coloré)', component: <HeroVariantB /> },
    C: { name: 'Minimal centré', component: <HeroVariantC /> },
    D: { name: 'Cards / Choix utilisateur', component: <HeroVariantD /> },
    E: { name: 'Brutalist / Bold', component: <HeroVariantE /> },
    F: { name: 'Glassmorphism / Futuriste', component: <HeroVariantF /> },
    G: { name: 'Editorial / Magazine', component: <HeroVariantG /> },
    H: { name: 'Playful / Friendly', component: <HeroVariantH /> },
    I: { name: 'Affiche émotionnelle', component: <HeroVariantI /> },
    J: { name: 'Immersive minimaliste', component: <HeroVariant_ImmersiveAI /> },
    K: { name: 'Néo-Rétro v2 (Affiche)', component: <HeroVariantK_NeoRetro /> },
    L: { name: 'Néo-Rétro v1 (Simple)', component: <HeroVariantL_NeoRetroV1 /> },
    M: { name: '🆕 Néo-Rétro Original', component: <HeroVariantM_NeoRetroOriginal /> },
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Active Variant Display */}
      <main className="pt-16 flex-1">
        {variants[activeVariant].component}
      </main>

      {/* Variant Selector - Bottom Fixed */}
      <div 
        className="sticky bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-t border-border shadow-lg"
        role="tablist"
        aria-label="Sélecteur de variantes Hero"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            <span className="text-sm font-semibold text-foreground whitespace-nowrap" id="variant-label">🎨 Variantes :</span>
            {(Object.keys(variants) as Array<keyof typeof variants>).map((key) => (
              <button
                key={key}
                onClick={() => setActiveVariant(key)}
                role="tab"
                aria-selected={activeVariant === key}
                aria-controls="hero-panel"
                tabIndex={activeVariant === key ? 0 : -1}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 ${
                  activeVariant === key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
              >
                {key}: {variants[key].name}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            Page non indexée — visible uniquement via /hero-test
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeroTest;
